firebird.factory('uniteRoomsAppNotificationService', [
    '$window',
    '$rootScope',
    '$interval',
    'logService',
    'localizationService',
    'avDeviceService',
    'subscriptionService',
    'socketService',
    function (
        $window,
        $rootScope,
        $interval,
        logService,
        localizationService,
        avDeviceService,
        subscriptionService,
        socketService
    ) {
        const logger = logService.createLoggerWithSource('uniteRoomsAppNotificationService');

        /* The service declaration */
        const service = {
            init: init,
            hasSwalNotification: hasSwalNotification,
            hasSwalCallback: hasSwalCallback,
            sendAvDeviceStatus: sendAvDeviceStatus
        };

        let latestSwalNotification = undefined;
        let latestSwalCallback = undefined;

        let lastDeviceStatus = undefined;
        let rootSubscription = undefined;

        const videoRoomBannerKey = 'unite-rooms-app-error-banner';

        $rootScope.$on('security-settings-changed', sendSecuritySettings);
        $rootScope.$on('$destroy', onScopeDestroyed);

        return service;

        /* The service implementation */
        function init() {
            rootSubscription = subscriptionService.createSubscription();

            patchBroadcast();
            patchGrowl();
            overrideSwal();
            startAvDevicesStatusChecking();
            subscribeOnErrorBanner();
            subscribeOnSocketMessages();
            subscribeOnMessages();
        }

        function subscribeOnSocketMessages() {
            if (!$window.preloadedApi.sendSocketMessage || !$window.preloadedApi.onSocketCommandReceived) {
                logger.warn(
                    'Unite rooms socket service cannot be initialized because of application version incompatibility. Skipping.'
                );
                return;
            }

            rootSubscription.add(socketService.clientMessage$.subscribe(socketEvent => sendSocketMessage(socketEvent)));
            rootSubscription.add(socketService.nodeConnected$.subscribe(state => sendSocketState(state)));

            subscribeOnSocketCommandReceived();
            subscribeOnSocketTerminated();
        }

        function sendSocketState(status) {
            if ($window.preloadedApi.sendSocketState) {
                $window.preloadedApi.sendSocketState(status);
            }
        }
        function sendSocketMessage(socketEvent) {
            if ($window.preloadedApi.sendSocketMessage) {
                $window.preloadedApi.sendSocketMessage(socketEvent);
            }
        }

        function subscribeOnSocketCommandReceived() {
            if ($window.preloadedApi.onSocketCommandReceived) {
                rootSubscription.add(
                    $window.preloadedApi.onSocketCommandReceived((_, command) => {
                        logger.log('onSocketCommandReceived', command);
                        command.callback = function (data) {
                            sendSocketMessage({ data, message: command.message });
                        };
                        socketService.send(command);
                    })
                );
            }
        }

        function subscribeOnSocketTerminated() {
            if ($window.preloadedApi.onSocketTerminated) {
                rootSubscription.add(
                    $window.preloadedApi.onSocketTerminated((_, options) => {
                        logger.log('onSocketTerminated', options);
                        const opt = options || {};
                        socketService.terminate(opt.attendeeId, { reason: opt.reason });
                    })
                );
            }
        }

        function overrideSwal() {
            logger.log('Overriding swal for video room application.');

            $window.swal = (options, cb) => {
                const notification = serializeSwalOptions(options);
                logger.log('Redirect an alert to video room application.', notification);

                latestSwalNotification = notification;
                latestSwalCallback = cb;

                $window.preloadedApi.sendNotification(notification);
            };

            subscribePreloadedApiEvents();
        }

        function hasSwalNotification() {
            return !!latestSwalNotification;
        }

        function hasSwalCallback() {
            return !!latestSwalCallback;
        }

        /* The service private functions */
        function subscribePreloadedApiEvents() {
            if (!$window.preloadedApi.onNotificationReceived) {
                logger.warn(
                    'subscribePreloadedApiEvents',
                    'Current version of video room application does not support callback handling.'
                );
                return;
            }

            // result: { id: "notification_id", value: "button_value" }
            rootSubscription.add(
                $window.preloadedApi.onNotificationReceived((_, result) => {
                    logger.log('Alert callback just received for video room application.', result);
                    const id = result.id;
                    if (!latestSwalNotification || id !== latestSwalNotification.id) {
                        logger.warn(
                            'subscribePreloadedApiEvents',
                            'Skip callback invocation, since mismatch of identity.'
                        );
                        return;
                    }

                    if (latestSwalCallback) {
                        // SweetAlert v1 supports only boolean true and false as buttons' values.
                        const value =
                            typeof result.value === 'string' ? result.value.toLowerCase() === 'true' : !!result.value;

                        latestSwalCallback(value);
                    } else {
                        logger.warn(
                            'subscribePreloadedApiEvents',
                            'Skip callback invocation, since no callback function registered.'
                        );
                    }

                    latestSwalNotification = undefined;
                    latestSwalCallback = undefined;
                })
            );
        }

        function serializeSwalOptions(swalOptions) {
            const swalBtns = [];
            let notificationText = swalOptions.text;
            let notificationTitle = swalOptions.title;

            if (swalOptions.showCancelButton) {
                swalBtns.push({
                    text: swalOptions.cancelButtonText || localizationService.translate('COMMON.CANCEL_TEXT'),
                    role: 'cancel',
                    // SweetAlert v1 uses boolean 'false' for cancellation action
                    value: 'false'
                });
            }

            if (swalOptions.showConfirmButton) {
                swalBtns.push({
                    text: swalOptions.confirmButtonText || localizationService.translate('COMMON.OK_TEXT'),
                    role: 'confirm',
                    // SweetAlert v1 uses boolean 'true' for confirmation action
                    value: 'true'
                });
            }

            if (swalOptions.html) {
                let parser = new DOMParser();
                notificationTitle = parser.parseFromString(swalOptions.title, 'text/html').documentElement.textContent;
                notificationText = parser.parseFromString(swalOptions.text, 'text/html').documentElement.textContent;
            }
            const type = getNotificationType(swalOptions);

            const notificationMessage = {
                id: getTimestamp(),
                title: notificationTitle,
                text: notificationText,
                type,
                buttons: swalBtns
            };

            return notificationMessage;
        }

        function getNotificationType(swalOptions) {
            if (swalOptions.extendedType) {
                return swalOptions.extendedType;
            }
            return swalOptions.type ? swalOptions.type : 'info';
        }

        function getTimestamp() {
            return new Date().getTime();
        }

        function patchGrowl() {
            if ($window.$.growl) {
                logger.log('Patching growl for video room application.');
                const growl = $window.$.growl;
                $window.$.growl = function () {
                    if (arguments[1]) {
                        arguments[1].allow_dismiss = false;
                        if (arguments[1].delay === 0) {
                            arguments[1].delay = 5000;
                        }
                    }
                    return growl.apply(this, arguments);
                };
            }
        }

        function patchBroadcast() {
            logger.log('videoRoomServiceAppNotificationService - patch broadcast');

            const mutedBroadcasts = {
                'show-modal': 1,
                'canvas-banner-message-show': 1
            };

            const Scope = $rootScope.constructor;
            const broadcast = Scope.prototype.$broadcast;
            Scope.prototype.$broadcast = function (name) {
                if (name === 'banner-message-show') {
                    arguments[1].hideDismissBtn = true;
                    if (arguments[1].key === undefined || arguments[1].key !== videoRoomBannerKey) {
                        sendBannerMessageShow();
                    }
                    return broadcast.apply(this, arguments);
                } else if (name === 'banner-message-dismiss') {
                    if (
                        arguments[1] === undefined ||
                        arguments[1].key === undefined ||
                        arguments[1].key !== videoRoomBannerKey
                    ) {
                        sendBannerMessageHide();
                    }
                }

                if (!mutedBroadcasts[name]) {
                    return broadcast.apply(this, arguments);
                }
            };
        }

        function sendBannerMessageShow() {
            if (!$window.preloadedApi.sendBannerShowNotification) {
                logger.warn(
                    'sendBannerMessageShow',
                    'Current version of video room application does not support banner show notification.'
                );
                return;
            }
            logger.log('Send banner show notification');
            $window.preloadedApi.sendBannerShowNotification();
        }

        function sendBannerMessageHide() {
            if (!$window.preloadedApi.sendBannerHideNotification) {
                logger.warn(
                    'sendBannerMessageHide',
                    'Current version of video room application does not support banner hide notification.'
                );
                return;
            }
            logger.log('Send banner hide notification');
            $window.preloadedApi.sendBannerHideNotification();
        }

        function startAvDevicesStatusChecking() {
            service.sendAvDeviceStatus();
            $interval(function () {
                service.sendAvDeviceStatus();
            }, 1000);
        }

        function sendAvDeviceStatus() {
            if ($window.preloadedApi.sendAvDeviceStatus) {
                let deviceStatus = {
                    mic: !avDeviceService.hasMicrophoneDeviceError() && avDeviceService.hasMicrophone(),
                    webcam: !avDeviceService.hasWebcamDeviceError() && avDeviceService.hasCamera()
                };
                if (
                    !lastDeviceStatus ||
                    lastDeviceStatus.mic !== deviceStatus.mic ||
                    lastDeviceStatus.webcam !== deviceStatus.webcam
                ) {
                    logger.log('sendAvDeviceStatus', deviceStatus);
                    $window.preloadedApi.sendAvDeviceStatus(deviceStatus);
                    lastDeviceStatus = deviceStatus;
                }
            }
        }

        function subscribeOnErrorBanner() {
            subscribeOnErrorBannerShowed();
            subscribeOnErrorBannerHided();
        }

        function subscribeOnErrorBannerShowed() {
            if (!$window.preloadedApi.onErrorBannerShowed) {
                logger.warn(
                    'onErrorBannerShowed',
                    'Current version of video room application does not support banner notification.'
                );
                return;
            }

            rootSubscription.add(
                $window.preloadedApi.onErrorBannerShowed((_, result) => {
                    logger.log('onErrorBannerShowed', result);

                    hideErrorBanner();

                    $rootScope.$broadcast('banner-message-show', {
                        key: videoRoomBannerKey,
                        header: result.header,
                        content: result.content,
                        persist: true,
                        hideDismissBtn: true
                    });
                })
            );
        }

        function subscribeOnErrorBannerHided() {
            if (!$window.preloadedApi.onErrorBannerHided) {
                logger.warn(
                    'onErrorBannerHided',
                    'Current version of video room application does not support banner notification.'
                );
                return;
            }

            rootSubscription.add(
                $window.preloadedApi.onErrorBannerHided(() => {
                    logger.log('onErrorBannerHided');
                    hideErrorBanner();
                })
            );
        }

        function hideErrorBanner() {
            $rootScope.$broadcast('banner-message-dismiss', {
                key: videoRoomBannerKey,
                forceDismiss: true
            });
        }

        function sendSecuritySettings(_, securitySettings) {
            if ($window.preloadedApi?.sendSecuritySettings) {
                logger.log('sendSecuritySettings', securitySettings);
                $window.preloadedApi.sendSecuritySettings(securitySettings);
            }
        }

        function subscribeOnMessages() {
            if (!$window.preloadedApi.onVideoRoomMessage) {
                logger.warn(
                    'onVideoRoomMessage',
                    'Current version of video room application does not support messaging.'
                );
                return;
            }

            // 'ipc-vr-message', { name: data.eventName, data: data.eventData }
            rootSubscription.add(
                $window.preloadedApi.onVideoRoomMessage((_, data) => {
                    logger.log('received video rooms message from ipc', data);
                    $rootScope.$broadcast(data.name, data.data);
                })
            );
        }

        function onScopeDestroyed() {
            if (rootSubscription) {
                subscriptionService.endSubscription(rootSubscription);
            }
        }
    }
]);
